import React, { useEffect, useState, useMemo } from 'react';
import { Button, Checkbox, Collapse, Drawer, Form, Input, Select, Upload } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import { UploadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { showErrorNotification } from '@med-fe/util';
import ExpandIcon from '../common/ExpandIcon';
import { createBrandedFixture, updateBrandedFixture, fetchVendorCollectionListings } from './branded-fixtures-actions';
import {
  getDetailsLoading,
  getUniqueVendors,
  getVendorCollectionListings,
  getVendorCollectionListingsLoading,
} from './branded-fixtures-selectors';
import { getIsWriteBrandedFixturesAllowed } from '../../store/user/user-reducer';
import { FixturePhoto } from '../../models/branded-fixture-dto';
import styles from '../collapsible-panel/CollapsiblePanel.module.scss';

const { TextArea } = Input;
const { Panel } = Collapse;

const CATEGORY_OPTIONS = [
  { label: 'Spinner', value: 'catSpinner' },
  { label: 'Wheels', value: 'catWheels' },
  { label: 'Fixed', value: 'catFixed' },
  { label: 'Double-Sided', value: 'catDoubleSided' },
  { label: 'Window Unit', value: 'catWindowUnit' },
  { label: 'Wall Unit', value: 'catWallUnit' },
  { label: 'Cabinet', value: 'catCabinet' },
  { label: 'Shelving', value: 'catShelving' },
];

const MAX_DECIMAL_INTEGER_DIGITS = 8;
const MAX_DECIMAL_FRACTIONAL_DIGITS = 2;
const MAX_CAPACITY_DIGITS = 10;
const SUPPORTED_PHOTO_EXTENSION = /\.(png|jpe?g)$/i;
const decimalPatternRule = { pattern: /^\d*\.?\d*$/, message: 'Only decimal values allowed' };
const decimalRangeRule = {
  validator: (_: unknown, value?: string | number) => {
    if (!value) return Promise.resolve();

    const [integerPart, fractionalPart = ''] = String(value).split('.');
    if (integerPart.length > MAX_DECIMAL_INTEGER_DIGITS || fractionalPart.length > MAX_DECIMAL_FRACTIONAL_DIGITS) {
      return Promise.reject(new Error('Enter up to 8 digits and 2 decimal places'));
    }

    return Promise.resolve();
  },
};
const capacityDigitCountRule = {
  validator: (_: unknown, value?: string | number) => {
    if (value === undefined || value === null || value === '') return Promise.resolve();

    if (String(value).length > MAX_CAPACITY_DIGITS) {
      return Promise.reject(new Error('Enter up to 10 digits'));
    }

    return Promise.resolve();
  },
};
const getIntegerCapacityValue = (event: React.ChangeEvent<HTMLInputElement>) =>
  event.target.value.replace(/\D/g, '').slice(0, MAX_CAPACITY_DIGITS);

interface IEditBrandedFixtureDetailsProps {
  visible: boolean;
  onClose: () => void;
  fixtureData?: any;
}

type FixturePhotoFile = UploadFile & { existingPhotoId?: number };

function EditBrandedFixtureDetails({ visible, onClose, fixtureData }: IEditBrandedFixtureDetailsProps) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const name = 'branded-fixture-details';
  const classNameForm = `${name}-form`;

  const [isDisabled, setIsDisabled] = useState(true);
  const [isGeneric, setIsGeneric] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);
  const [photoFiles, setPhotoFiles] = useState<FixturePhotoFile[]>([]);

  const vendors = useSelector(getUniqueVendors);
  const vendorCollectionListings = useSelector(getVendorCollectionListings);
  const vendorCollectionListingsLoading = useSelector(getVendorCollectionListingsLoading);
  const detailsLoading = useSelector(getDetailsLoading);
  const isEditAllowed = useSelector(getIsWriteBrandedFixturesAllowed);

  const isEditMode = !!fixtureData?.id;

  // Get collections for the selected vendor
  const collectionsForVendor = useMemo(() => {
    if (!selectedVendorId || !vendorCollectionListings) return [];
    return vendorCollectionListings
      .filter((item: any) => item.acuityVendorID === selectedVendorId && item.collectionName)
      .map((item: any) => ({
        acuityCollectionID: item.acuityCollectionID,
        collectionName: item.collectionName,
        databaseCollectionID: item.databaseCollectionID,
        collectionType: item.collectionType,
        notes: item.notes,
      }))
      .sort((a: any, b: any) => (a.collectionName || '').localeCompare(b.collectionName || ''));
  }, [selectedVendorId, vendorCollectionListings]);

  // Fetch vendor-collection listings on mount
  useEffect(() => {
    dispatch(fetchVendorCollectionListings());
  }, [dispatch]);

  useEffect(() => {
    if (visible) {
      setPhotoFiles(
        (fixtureData?.photos || []).map((photo: FixturePhoto, index: number) => ({
          uid: `existing-${photo.id}`,
          name: `Photo ${index + 1}`,
          status: 'done' as const,
          existingPhotoId: photo.id,
        }))
      );
    }
  }, [visible, fixtureData]);

  useEffect(() => {
    if (visible && fixtureData) {
      // Convert individual category booleans to array for multi-select
      const selectedCategories: string[] = [];
      if (fixtureData.catSpinner) selectedCategories.push('catSpinner');
      if (fixtureData.catWheels) selectedCategories.push('catWheels');
      if (fixtureData.catFixed) selectedCategories.push('catFixed');
      if (fixtureData.catDoubleSided) selectedCategories.push('catDoubleSided');
      if (fixtureData.catWindowUnit) selectedCategories.push('catWindowUnit');
      if (fixtureData.catWallUnit) selectedCategories.push('catWallUnit');
      if (fixtureData.catCabinet) selectedCategories.push('catCabinet');
      if (fixtureData.catShelving) selectedCategories.push('catShelving');

      setIsGeneric(fixtureData.isGeneric || false);

      // Find vendor ID from vendor name
      const matchedVendor = vendors?.find((v: any) => v.vendorName === fixtureData.vendorName);
      const vendorId = matchedVendor?.acuityVendorID || null;
      setSelectedVendorId(vendorId);

      // Find collection ID from collection name (need to search in listings for the vendor)
      const matchedCollection = vendorCollectionListings?.find(
        (item: any) => item.acuityVendorID === vendorId && item.collectionName === fixtureData.collection
      );
      const collectionId = matchedCollection?.acuityCollectionID || null;

      const formValues = {
        acuityVendorID: vendorId,
        acuityCollectionID: collectionId,
        isGeneric: fixtureData.isGeneric || false,
        fixtureName: fixtureData.fixtureName,
        description: fixtureData.description,
        active: fixtureData.active !== false, // default to true
        boardCapacity: fixtureData.boardCapacity ?? fixtureData.board_capacity ?? fixtureData.capacity,
        storageCapacity: fixtureData.storageCapacity,
        dimensionWidth: fixtureData.dimensionWidth,
        dimensionHeight: fixtureData.dimensionHeight,
        dimensionDepth: fixtureData.dimensionDepth,
        electricityRequired: fixtureData.electricityRequired || false,
        lockable: fixtureData.lockable || false,
        storageDrawers: fixtureData.storageDrawers || false,
        categories: selectedCategories,
        notes: fixtureData.notes,
      };
      form.setFieldsValue(formValues);
    } else if (visible) {
      form.resetFields();
      setIsGeneric(false);
      setSelectedVendorId(null);
      // Set default values for new fixture
      form.setFieldsValue({ active: true, categories: [], isGeneric: false });
    }
  }, [visible, fixtureData, form, vendors, vendorCollectionListings]);

  const onSubmit = (formData: any) => {
    const selectedVendor = vendors?.find((v: any) => v.acuityVendorID === formData.acuityVendorID);
    const selectedCollection = collectionsForVendor?.find(
      (c: any) => c.acuityCollectionID === formData.acuityCollectionID
    );
    const selectedCategories = formData.categories || [];

    const payload = {
      vendorSid: formData.isGeneric ? null : formData.acuityVendorID || null,
      vendorName: formData.isGeneric ? null : selectedVendor?.vendorName || null,
      collection: formData.isGeneric ? null : selectedCollection?.collectionName || null,
      isGeneric: formData.isGeneric || false,
      fixtureName: formData.fixtureName,
      description: formData.description || null,
      boardCapacity: formData.boardCapacity ? parseInt(formData.boardCapacity) : null,
      storageCapacity: formData.storageCapacity ? parseInt(formData.storageCapacity) : null,
      dimensionWidth: formData.dimensionWidth || null,
      dimensionHeight: formData.dimensionHeight || null,
      dimensionDepth: formData.dimensionDepth || null,
      electricityRequired: formData.electricityRequired || false,
      lockable: formData.lockable || false,
      storageDrawers: formData.storageDrawers || false,
      catSpinner: selectedCategories.includes('catSpinner'),
      catWheels: selectedCategories.includes('catWheels'),
      catFixed: selectedCategories.includes('catFixed'),
      catDoubleSided: selectedCategories.includes('catDoubleSided'),
      catWindowUnit: selectedCategories.includes('catWindowUnit'),
      catWallUnit: selectedCategories.includes('catWallUnit'),
      catCabinet: selectedCategories.includes('catCabinet'),
      catShelving: selectedCategories.includes('catShelving'),
      notes: formData.notes || null,
      active: formData.active !== false,
    };

    const photos = photoFiles.map((file) => file.originFileObj).filter(Boolean);

    if (isEditMode) {
      const retainedPhotoIds = new Set(photoFiles.map((file) => file.existingPhotoId));
      const removedPhotos = ((fixtureData.photos || []) as FixturePhoto[])
        .filter((photo) => !retainedPhotoIds.has(photo.id))
        .map((photo) => ({
          brandedFixtureSid: photo.brandedFixtureSid,
          brandedFixturePhotoSid: photo.id,
          photoUrl: photo.photoUrl,
        }));
      dispatch(updateBrandedFixture({ id: fixtureData.id, ...payload, photos, removedPhotos }));
    } else {
      dispatch(createBrandedFixture({ ...payload, photos }));
    }

    setIsDisabled(true);
    setIsGeneric(false);
    setSelectedVendorId(null);
    setPhotoFiles([]);
    form.resetFields();
    onClose();
  };

  const handleCancel = () => {
    form.resetFields();
    setIsDisabled(true);
    setIsGeneric(false);
    setSelectedVendorId(null);
    setPhotoFiles([]);
    onClose();
  };

  const handleVendorChange = (value: number | undefined) => {
    setSelectedVendorId(value || null);
    // Clear collection when vendor changes
    form.setFieldsValue({ acuityCollectionID: undefined });
  };

  return (
    <Drawer
      placement='right'
      open={visible}
      width={400}
      closable={false}
      className='drawer-panel'
      forceRender={true}
      onClose={handleCancel}
    >
      <div className={styles['details-panel']}>
        <div className={styles['details-panel-header']}>
          <span>{isEditMode ? 'UPDATE BRANDED FIXTURE' : 'ADD BRANDED FIXTURE'}</span>
          <Button type={'ghost'} className={styles['details-panel-btn']} onClick={handleCancel}>
            CLOSE ALL &#10006;
          </Button>
        </div>
        <Collapse
          destroyInactivePanel
          defaultActiveKey={1}
          bordered={true}
          expandIcon={({ isActive }) => <ExpandIcon isActive={isActive} />}
          className='med-custom-collapse'
          ghost={true}
        >
          <Panel header='FIXTURE DETAILS' key={1} className='med-custom-panel'>
            <Form
              form={form}
              name={name}
              layout='vertical'
              onFinish={onSubmit}
              autoComplete='off'
              className={classNameForm}
              onValuesChange={(changedValues) => {
                setIsDisabled(false);
                if ('isGeneric' in changedValues) {
                  const genericValue = changedValues.isGeneric;
                  setIsGeneric(genericValue);
                  if (genericValue) {
                    // Clear Vendor and Collection when Generic is checked
                    form.setFieldsValue({ acuityVendorID: undefined, acuityCollectionID: undefined });
                    setSelectedVendorId(null);
                  }
                }
              }}
              disabled={!isEditAllowed}
            >
              {/* Generic */}
              <Form.Item key='isGeneric' name='isGeneric' valuePropName='checked' className='label-text'>
                <Checkbox>Generic</Checkbox>
              </Form.Item>

              {/* Vendor */}
              <Form.Item key='acuityVendorID' label='Product Vendor' name='acuityVendorID' className='label-text'>
                <Select
                  placeholder='Select Product Vendor'
                  allowClear
                  showSearch
                  optionFilterProp='children'
                  disabled={isGeneric}
                  loading={vendorCollectionListingsLoading}
                  onChange={handleVendorChange}
                >
                  {vendors?.map((vendor: any) => (
                    <Select.Option key={vendor.acuityVendorID} value={vendor.acuityVendorID}>
                      {vendor.vendorName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Collection */}
              <Form.Item key='acuityCollectionID' label='Collection' name='acuityCollectionID' className='label-text'>
                <Select
                  placeholder='Select Collection'
                  allowClear
                  showSearch
                  optionFilterProp='children'
                  disabled={isGeneric || !selectedVendorId}
                >
                  {collectionsForVendor?.map((collection: any) => (
                    <Select.Option key={collection.acuityCollectionID} value={collection.acuityCollectionID}>
                      {collection.collectionName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Fixture Name */}
              <Form.Item
                key='fixtureName'
                label='Fixture Name'
                name='fixtureName'
                className='label-text'
                rules={[{ required: true, message: 'Please enter fixture name' }]}
              >
                <Input placeholder='Enter fixture name' />
              </Form.Item>

              {/* Description */}
              <Form.Item key='description' label='Description' name='description' className='label-text'>
                <TextArea rows={3} placeholder='Enter description' />
              </Form.Item>

              {/* Status (Active) */}
              <Form.Item key='active' name='active' valuePropName='checked' className='label-text'>
                <Checkbox>Active</Checkbox>
              </Form.Item>

              {/* Board Capacity */}
              <Form.Item
                key='boardCapacity'
                label='Board Capacity'
                name='boardCapacity'
                className='label-text'
                getValueFromEvent={getIntegerCapacityValue}
                rules={[{ pattern: /^\d*$/, message: 'Only numeric values allowed' }, capacityDigitCountRule]}
              >
                <Input
                  placeholder='Enter board capacity'
                  maxLength={10}
                  style={{ width: '100%' }}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                      return;
                    }

                    const input = e.currentTarget;
                    const hasSelection = input.selectionStart !== input.selectionEnd;
                    if (input.value.length >= 10 && !hasSelection) {
                      form.setFields([{ name: 'boardCapacity', errors: ['Enter up to 10 digits'] }]);
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>

              {/* Storage Capacity */}
              <Form.Item
                key='storageCapacity'
                label='Storage Capacity'
                name='storageCapacity'
                className='label-text'
                getValueFromEvent={getIntegerCapacityValue}
                rules={[{ pattern: /^\d*$/, message: 'Only numeric values allowed' }, capacityDigitCountRule]}
              >
                <Input
                  placeholder='Enter storage capacity'
                  maxLength={10}
                  style={{ width: '100%' }}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                      return;
                    }

                    const input = e.currentTarget;
                    const hasSelection = input.selectionStart !== input.selectionEnd;
                    if (input.value.length >= 10 && !hasSelection) {
                      form.setFields([{ name: 'storageCapacity', errors: ['Enter up to 10 digits'] }]);
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>

              {/* Dimensions */}
              <Form.Item label='Dimensions' className='label-text' style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Form.Item name='dimensionWidth' noStyle rules={[decimalPatternRule, decimalRangeRule]}>
                    <Input
                      placeholder='Width'
                      style={{ width: '70px' }}
                      maxLength={11}
                      onKeyPress={(e) => {
                        if (!/[0-9.]/.test(e.key) || (e.key === '.' && e.currentTarget.value.includes('.'))) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                  <span>X</span>
                  <Form.Item name='dimensionHeight' noStyle rules={[decimalPatternRule, decimalRangeRule]}>
                    <Input
                      placeholder='Height'
                      style={{ width: '70px' }}
                      maxLength={11}
                      onKeyPress={(e) => {
                        if (!/[0-9.]/.test(e.key) || (e.key === '.' && e.currentTarget.value.includes('.'))) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                  <span>X</span>
                  <Form.Item name='dimensionDepth' noStyle rules={[decimalPatternRule, decimalRangeRule]}>
                    <Input
                      placeholder='Depth'
                      style={{ width: '70px' }}
                      maxLength={11}
                      onKeyPress={(e) => {
                        if (!/[0-9.]/.test(e.key) || (e.key === '.' && e.currentTarget.value.includes('.'))) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </div>
              </Form.Item>

              {/* Electricity Required */}
              <Form.Item name='electricityRequired' valuePropName='checked' className='label-text'>
                <Checkbox>Electricity Required</Checkbox>
              </Form.Item>

              {/* Lockable */}
              <Form.Item name='lockable' valuePropName='checked' className='label-text'>
                <Checkbox>Lockable</Checkbox>
              </Form.Item>

              {/* Storage or Drawers */}
              <Form.Item name='storageDrawers' valuePropName='checked' className='label-text'>
                <Checkbox>Storage or Drawers Available</Checkbox>
              </Form.Item>

              {/* Categories */}
              <Form.Item key='categories' label='Categories' name='categories' className='label-text'>
                <Select
                  mode='multiple'
                  className='categories-select'
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  maxTagCount='responsive'
                  maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                  placeholder='Select categories'
                  allowClear
                  options={CATEGORY_OPTIONS}
                />
              </Form.Item>

              {/* Notes */}
              <Form.Item key='notes' label='Notes' name='notes' className='label-text'>
                <TextArea rows={3} placeholder='Enter notes' />
              </Form.Item>

              <Form.Item label='Photos' className='label-text'>
                <Upload
                  key={fixtureData?.id ?? 'new'}
                  accept='.png,.jpg,.jpeg'
                  listType='text'
                  showUploadList={{ showPreviewIcon: false }}
                  fileList={
                    visible
                      ? photoFiles.filter(
                          (file) =>
                            !file.existingPhotoId ||
                            fixtureData?.photos?.some((photo: FixturePhoto) => photo.id === file.existingPhotoId)
                        )
                      : []
                  }
                  maxCount={3}
                  disabled={!isEditAllowed}
                  beforeUpload={(file) => {
                    if (!SUPPORTED_PHOTO_EXTENSION.test(file.name)) {
                      showErrorNotification('You can only upload JPG/JPEG/PNG file!');
                      return Upload.LIST_IGNORE;
                    }

                    return false;
                  }}
                  onChange={({ fileList }) => {
                    setPhotoFiles(fileList.slice(0, 3));
                    setIsDisabled(false);
                  }}
                >
                  {photoFiles.length < 3 && (
                    <Button type='text' icon={<UploadOutlined />} disabled={!isEditAllowed}>
                      Add Photo
                    </Button>
                  )}
                </Upload>
              </Form.Item>

              {/* Action Buttons */}
              <Form.Item>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    type='primary'
                    htmlType='submit'
                    disabled={isDisabled}
                    loading={detailsLoading}
                    className='btn-form-action'
                  >
                    SAVE
                  </Button>
                  <Button onClick={handleCancel} className='btn-form-action'>
                    CANCEL
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </Panel>
        </Collapse>
      </div>
    </Drawer>
  );
}

export default EditBrandedFixtureDetails;
